import React, { useState, useRef, useCallback, useEffect } from "react"
import { Box, Drawer, List, makeStyles, Divider, Button, useMediaQuery } from "@material-ui/core"
import { NavLink, useLocation } from "react-router-dom"
import { t } from "@lingui/macro"
import Config from "react-global-configuration"
import { useAuth } from "../../services"
import { useMutationUpdatePreferences, useQuerySettings } from "../../data"
import { useQueryAreas } from "../../data/areas/useQueryAreas"
import { Icon } from "../Icon"
import { RequirePermissions } from "../Permissions/RequirePermissions"
import { getNavItems } from "./NavItems"
import { RowBox } from "../Boxes"
import { mapToIds, toId } from "../../utils"
import { NewActionButton } from "../NewActionButton"
import { usePunchClockState } from "../../data/timesheets/punchClockStateVar"
import useOnClickOutside from "../../utils/useOnClickOutside"
import useTouch from "../../utils/useTouch"

const useStyles = makeStyles((theme) => {
  const { header, punchClock, leftNavigation } = theme.dimensions

  return {
    container: {
      minWidth: leftNavigation.drawer.collapsed,
      width: ({ isPinned, isHoverMode }) =>
        isHoverMode
          ? leftNavigation.drawer.collapsed
          : isPinned
            ? leftNavigation.drawer.expanded
            : leftNavigation.drawer.collapsed,
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      }),
      position: "relative"
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0)",
      zIndex: 1290,
      opacity: ({ isHoverMode, expanded }) => (isHoverMode && expanded ? 1 : 0),
      visibility: ({ isHoverMode, expanded }) => (isHoverMode && expanded ? "visible" : "hidden"),
      transition: theme.transitions.create(["opacity", "visibility"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      })
    },
    drawer: {
      width: ({ expanded }) => (expanded ? leftNavigation.drawer.expanded : leftNavigation.drawer.collapsed),
      flexShrink: 0,
      whiteSpace: "nowrap",
      position: "fixed",
      zIndex: ({ isHoverMode }) => (isHoverMode ? 1300 : 1250),
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      })
    },
    drawerPaper: {
      width: ({ expanded }) => (expanded ? leftNavigation.drawer.expanded : leftNavigation.drawer.collapsed),
      border: 0,
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      }),
      padding: theme.spacing(2),
      borderTop: 0,
      height: ({ hasPunchClock }) => `calc(100dvh - ${header.height + (hasPunchClock ? punchClock.height : 0)}px)`,
      display: "flex",
      flexDirection: "column",
      position: "sticky",
      top: header.height,
      boxShadow: "none",
      backgroundColor: theme.palette.background.paper
    },
    toggleButton: {
      position: "absolute",
      right: -12,
      bottom: 100,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: theme.radius.full,
      "&:hover": {
        backgroundColor: theme.palette.grey[100]
      }
    },
    navItem: {
      display: "flex",
      alignItems: "center",
      color: theme.palette.text.primary,
      textDecoration: "none",
      borderRadius: theme.radius(1),
      "&:hover": {
        background: theme.palette.action.hover,
        transition: "all 0.2s ease"
      },
      "&:focus-visible": {
        background: theme.palette.action.focus,
        outline: "none"
      },
      "&.active": {
        backgroundColor: theme.palette.action.selected
      }
    },
    iconBox: {
      alignItems: "center",
      justifyContent: "flex-start",
      display: "flex",
      flex: 1,
      gap: theme.spacing(1.5),
      width: ({ expanded }) => (expanded ? "100%" : 36),
      transition: theme.transitions.create(["width", "justify-content"]),
      position: "relative",
      margin: "0 auto",
      height: leftNavigation.item.height,
      padding: 6
    },
    icon: {
      width: leftNavigation.item.iconSize,
      minWidth: leftNavigation.item.iconSize,
      display: "flex"
    },
    label: {
      opacity: ({ expanded }) => (expanded ? 1 : 0),
      transition: theme.transitions.create("opacity"),
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    list: {
      display: "flex",
      flexDirection: "column",
      color: theme.palette.text.primary,
      fontSize: 16,
      fontWeight: 600,
      overflowY: "auto",
      overscrollBehavior: "contain",
      flex: 1,
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
      marginTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.grey[200]}`,
      scrollbarWidth: ({ expanded }) => (expanded ? "inherit" : "none")
    },
    sectionDivider: {
      borderColor: theme.palette.grey[300],
      height: 1,
      width: "100%"
    },
    sectionLabel: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      fontWeight: 500,
      padding: theme.spacing(0, 2, 0, 1),
      opacity: ({ expanded }) => (expanded ? 1 : 0),
      transition: theme.transitions.create("opacity")
    },
    dividerSection: {
      paddingRight: ({ expanded }) => (expanded ? theme.spacing(4) : theme.spacing(0)),
      maxWidth: leftNavigation.drawer.expanded,
      position: "relative",
      alignItems: "center",
      overflow: "hidden",
      padding: theme.spacing(1, 0),
      height: leftNavigation.item.height
    },
    bottomActions: {
      marginTop: "auto",
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.grey[200]}`,
      width: ({ expanded }) => (expanded ? "100%" : leftNavigation.item.height)
    },
    bottomNavItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      color: theme.palette.text.secondary,
      textDecoration: "none",
      borderRadius: theme.radius(1),
      cursor: "pointer",
      "&:hover": {
        background: theme.palette.action.hover,
        transition: "all 0.2s ease"
      },
      "&:focus-visible": {
        background: theme.palette.action.focus,
        outline: "none",
        boxShadow: "none"
      },
      height: leftNavigation.item.height,
      width: ({ expanded }) => (expanded ? "100%" : leftNavigation.item.width),
      minWidth: 36,
      transition: "0.2s all"
    },
    bottomLabel: {
      fontSize: 14,
      fontWeight: 400,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      opacity: ({ expanded }) => (expanded ? 1 : 0),
      marginLeft: theme.spacing(0.5)
    },
    mainItems: {
      flex: 1,
      width: ({ expanded }) => (expanded ? "100%" : leftNavigation.item.width),
      transition: theme.transitions.create("width"),
      gap: theme.spacing(1),
      display: "flex",
      flexDirection: "column"
    },
    scrollShadows: {
      background: [
        `linear-gradient(${theme.palette.background.default} 30%, ${theme.palette.background.default}00) center top`,
        `linear-gradient(${theme.palette.background.default}00, ${theme.palette.background.default} 70%) center bottom`,
        `radial-gradient(farthest-side at 50% 0, ${theme.palette.background.dark}33, ${theme.palette.background.dark}00) center top`,
        `radial-gradient(farthest-side at 50% 100%, ${theme.palette.background.dark}33, ${theme.palette.background.dark}00) center bottom`
      ].join(", "),
      backgroundRepeat: "no-repeat",
      backgroundSize: "100% 40px, 100% 40px, 100% 14px, 100% 14px",
      backgroundAttachment: "local, local, scroll, scroll"
    }
  }
})

const isSwipeRight = (dx, threshold = 30) => {
  return Math.abs(dx) > threshold && dx > 0
}

const isSwipeLeft = (dx, threshold = 30) => {
  return Math.abs(dx) > threshold && dx < 0
}

export const LeftNavigation = () => {
  const md = useMediaQuery((theme) => theme.breakpoints.down("md"))
  const {
    permissionGroups: { readReports, readTemplates, readUsers, adminTraining },
    featureDependencies,
    location: userLocation,
    principal: { groups: usersGroups },
    settings: { preferences },
    hasFeature,
    hasPermission
  } = useAuth()
  const [{ show: hasPunchClock }] = usePunchClockState()
  const [updatePreferences] = useMutationUpdatePreferences()
  const initialPinned = !md && preferences?.pinPrimaryNavigation
  const [expanded, setExpanded] = useState(initialPinned)
  const [isPinned, setIsPinned] = useState(initialPinned)
  const [isHovered, setIsHovered] = useState(false)
  const [isHoverMode, setIsHoverMode] = useState(false)
  const [showActionMenu, setShowActionMenu] = useState(false)
  const classes = useStyles({
    expanded: expanded || (isHovered && !isPinned) || (isHoverMode && expanded),
    isPinned,
    isHoverMode,
    hasPunchClock
  })
  const location = useLocation()
  const { lang } = useQuerySettings()
  const langArea = lang.area
  const { data: areaData } = useQueryAreas({
    variables: {
      filter: {
        locations: [toId(userLocation)],
        groups: mapToIds(usersGroups)
      }
    }
  })
  const { clientKiosk: kiosk, clientDevice: device } = Config.get()
  const isDevice = kiosk || device
  const shouldUseHoverMode = md || isDevice
  const drawerRef = useRef(null)
  const { events: touchEvents } = useTouch({
    onMove: (_, data) => {
      const dx = data.dx
      if (!expanded && !isHoverMode && isSwipeRight(dx)) {
        setIsHovered(true)
      } else if (isHovered && !isHoverMode && isSwipeLeft(dx)) {
        setIsHovered(false)
      }
    },
    onEnd: (_, data) => {
      const dx = data.dx
      if (isSwipeRight(dx, 100)) {
        setIsPinned(true)
        setIsHoverMode(true)
        setExpanded(true)
      } else if (isSwipeLeft(dx, 100)) {
        setIsPinned(false)
        setIsHoverMode(false)
        setIsHovered(false)
        setExpanded(false)
      }
    },
    skip: showActionMenu
  })

  const handleMouseEnter = useCallback(() => {
    if (isDevice || isHoverMode) {
      return
    }

    if (!isPinned) {
      setIsHovered(true)
    }
  }, [isPinned, isDevice, isHoverMode])

  const handleMouseLeave = useCallback(
    (event) => {
      const isInBrowserViewport = !(
        !(event.relatedTarget instanceof Window) ||
        event?.relatedTarget === null ||
        event?.toElement === null
      )

      if (isInBrowserViewport && showActionMenu) {
        return
      }

      if (!isPinned && !isHoverMode) {
        setIsHovered(false)
      }
    },
    [isPinned, isHoverMode, showActionMenu]
  )

  const handleToggle = useCallback(() => {
    const newPinnedState = !isPinned

    setIsPinned(newPinnedState)
    setExpanded(newPinnedState)

    if (shouldUseHoverMode) {
      // For mobile or device, use hover mode instead of pinned mode
      setIsHovered(newPinnedState)
      setIsHoverMode(newPinnedState)
      return
    }

    updatePreferences({
      variables: {
        input: {
          pinPrimaryNavigation: newPinnedState
        }
      }
    })
  }, [isPinned, shouldUseHoverMode, updatePreferences])

  const handleCloseHoverMenu = useCallback(() => {
    if (isHoverMode) {
      setExpanded(false)
      setIsHoverMode(false)
      setIsPinned(false)
      setIsHovered(false)
    }
  }, [isHoverMode])

  const { mainItems, bottomItems } = getNavItems({
    location,
    langArea,
    areaData,
    hasFeature,
    hasPermission,
    readReports,
    readTemplates,
    readUsers,
    adminTraining,
    userLocation,
    featureDependencies
  })

  const modifiedBottomItems = bottomItems.map((item) => ({
    ...item,
    text: expanded || isPinned || isHoverMode ? t`Minimize` : t`Keep open`,
    expandedText: expanded || isPinned || isHoverMode ? t`Minimize` : t`Keep open`,
    icon: isPinned || isHoverMode ? "menu-close" : "menu-open",
    expandedIcon: isPinned || isHoverMode ? "menu-close" : "menu-open"
  }))

  useOnClickOutside(drawerRef, () => {
    // Close the hover menu if the user clicks outside of the drawer
    // and the drawer is not pinned
    if (isPinned && isHoverMode && !showActionMenu) {
      setExpanded(false)
      setIsHovered(false)
      setIsPinned(false)
    }
  })

  useEffect(() => {
    if (shouldUseHoverMode) {
      setIsHoverMode(shouldUseHoverMode)
      setIsPinned(false)
      setExpanded(false)
    }
  }, [shouldUseHoverMode])

  return (
    <>
      <Box className={classes.overlay} onClick={handleCloseHoverMenu} />
      <Box className={classes.container}>
        <Drawer
          variant="permanent"
          className={classes.drawer}
          classes={{
            paper: classes.drawerPaper
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...touchEvents}
          PaperProps={{ ref: drawerRef }}
        >
          <NewActionButton
            expanded={expanded || (isHovered && !isPinned) || (isHoverMode && expanded)}
            onOpen={() => {
              setShowActionMenu(true)
            }}
            onClose={(shouldCloseLeftNavigation) => {
              setShowActionMenu(false)
              if (!shouldCloseLeftNavigation) {
                setTimeout(() => {
                  setIsHovered(false)
                }, 100)
              }
            }}
            drawerRef={drawerRef}
          />
          <List className={`${classes.list} ${classes.scrollShadows}`}>
            <Box className={classes.mainItems}>
              {mainItems.map(
                (section, index) =>
                  (!section.requires || hasPermission(section.requires)) && (
                    <React.Fragment key={section.section}>
                      {index > 0 && (
                        <RowBox className={classes.dividerSection}>
                          {(expanded || (isHovered && !isPinned) || (isHoverMode && expanded)) && (
                            <Box className={classes.sectionLabel}>{section.section}</Box>
                          )}
                          <Divider className={classes.sectionDivider} />
                        </RowBox>
                      )}
                      {section.items.map((item) => {
                        if (item.requires && !hasPermission(item.requires)) return null
                        if (item.feature && !hasFeature(item.feature)) return null

                        // Ensure is active is a function
                        const isActive = typeof item.selected === "function" ? item.selected : () => false

                        return (
                          <RequirePermissions key={item.to} requires={item.requires} operator={item.operator}>
                            <NavLink
                              to={item.to}
                              className={classes.navItem}
                              activeClassName="active"
                              disabled={item.disabled}
                              isActive={isActive}
                              onClick={handleCloseHoverMenu}
                            >
                              <Box className={classes.iconBox}>
                                <Icon name={item.icon} className={classes.icon} />
                                {(expanded || (isHovered && !isPinned) || (isHoverMode && expanded)) && (
                                  <Box className={classes.label}>{item.text}</Box>
                                )}
                              </Box>
                            </NavLink>
                          </RequirePermissions>
                        )
                      })}
                    </React.Fragment>
                  )
              )}
            </Box>
          </List>

          <Box className={classes.bottomActions}>
            {modifiedBottomItems.map((item) => (
              <Button key={item.id} onClick={handleToggle} className={classes.bottomNavItem}>
                <Icon name={item.expandedIcon} className={classes.icon} />
                {(expanded || (isHovered && !isPinned) || (isHoverMode && expanded)) && (
                  <Box className={classes.bottomLabel}>{md ? t`Minimize` : item.text}</Box>
                )}
              </Button>
            ))}
          </Box>
        </Drawer>
      </Box>
    </>
  )
}
